/**
 * Placed images, inlined at RENDER time.
 *
 * A placed picture is written into the document as `<img data-deck-image="<id>" src="/api/v1/storage/…">`,
 * the stored URL and nothing more, so the document stays small (an inlined 465 KB screenshot put one
 * artifact over the 60 KB contract cap and into every 600 ms autosave). The document renders in a
 * sandboxed frame on an opaque origin, and that frame cannot fetch the URL itself: Chrome blocks an
 * opaque origin from reaching localhost outright, and on prod the storage route wants the cookie the
 * frame cannot send. The parent page can fetch it, with credentials, so it does, and hands the frame a
 * data URI in the src instead. Same trick `captureKitSite` uses for the kit mark.
 *
 * Only `data-deck-image` imgs are touched. Everything else in the document is the author's business.
 */
import { useEffect, useState } from 'react';

const DECK_IMG_RE = /<img\b[^>]*\bdata-deck-image=[^>]*>/gi;
const SRC_RE = /\bsrc="([^"]*)"/i;

/** Fetched once per URL for the life of the page; a deck of ten artifacts sharing a logo fetches it once. */
const cache = new Map<string, Promise<string | null>>();

export function needsInlining(html: string): boolean {
  return /\bdata-deck-image=/.test(html);
}

/** A picture in the deck's own storage: the only kind a placed picture may point at (validator REJECT 8). */
export function isStoredUrl(src: string): boolean {
  return /^(?:https?:\/\/[^/]+)?\/api\/v1\/storage\//.test(src);
}

/**
 * A picture's bytes as a data URI, fetched by the page (which can) for a frame (which cannot). Shared with
 * the hand-added picture from the Directory (`useArtifactImages`), which needs the same bytes for the live
 * frame that the inlining gives the next mount.
 */
export function toDataUrl(src: string): Promise<string | null> {
  const hit = cache.get(src);
  if (hit) return hit;
  const pending = fetch(src, { credentials: 'include' })
    .then((res) => (res.ok ? res.blob() : null))
    .then(
      (blob) =>
        new Promise<string | null>((resolve) => {
          if (!blob) return resolve(null);
          const reader = new FileReader();
          reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        }),
    )
    .catch(() => null);
  cache.set(src, pending);
  return pending;
}

/**
 * The bytes for a stored URL, handed over by whoever already has them: a picture the user just added by hand
 * was read from their own file, so the next mount (an undo, coming back to the artifact) inlines it without
 * a round trip. ALWAYS overwrites. A fetch that failed is cached as null for the life of the page, and one
 * made in the moment between an upload and its storage settling must not leave the picture broken.
 */
export function primeDeckImage(src: string, dataUrl: string): void {
  if (!src || !dataUrl.startsWith('data:image/')) return;
  cache.set(src, Promise.resolve(dataUrl));
}

/** The document with every placed image's stored URL swapped for its bytes. Unfetchable ones are left as they are. */
export async function inlineDeckImages(html: string): Promise<string> {
  if (!needsInlining(html)) return html;
  const tags = html.match(DECK_IMG_RE) ?? [];
  const srcs = Array.from(new Set(tags.map((t) => t.match(SRC_RE)?.[1]).filter((s): s is string => !!s && isStoredUrl(s))));
  if (srcs.length === 0) return html;
  const data = await Promise.all(srcs.map(toDataUrl));
  const byUrl = new Map(srcs.map((s, i) => [s, data[i]]));
  return html.replace(DECK_IMG_RE, (tag) => {
    const src = tag.match(SRC_RE)?.[1];
    const inlined = src ? byUrl.get(src) : null;
    return inlined ? tag.replace(SRC_RE, `src="${inlined}"`) : tag;
  });
}

/**
 * The document to hand a frame: the same string when it holds no placed image, the inlined one once
 * the bytes arrive, and NULL in between. Null means "do not mount the frame yet". A frame that is
 * mounted with the URL form and then handed the inlined form as a second srcdoc paints white in the
 * preview (measured 2026-09-08: the second document loads but never paints inside the scaled stage),
 * so a frame gets exactly one document, and the caller shows its pending surface until it is ready.
 */
export function useInlinedDeckImages(html: string): string | null {
  const [inlined, setInlined] = useState<{ from: string; to: string } | null>(null);
  const pending = needsInlining(html);
  useEffect(() => {
    if (!pending) return;
    let live = true;
    void inlineDeckImages(html).then((to) => {
      if (live) setInlined({ from: html, to });
    });
    return () => {
      live = false;
    };
  }, [html, pending]);
  if (!pending) return html;
  return inlined && inlined.from === html ? inlined.to : null;
}

/**
 * The inverse, for what gets STORED. A bake is rendered from the inlined document (the frame cannot
 * fetch), but persisted with the stored URLs put back: nine inlined pictures made a 4.7 MB deck save
 * that the backend refused (2026-09-08, "this thumbnail is not updating"), so no bake ever landed.
 * Pictures are matched by their `data-deck-image` id against the source document the bake came from.
 */
export function restoreDeckImageUrls(inlined: string, source: string): string {
  if (!needsInlining(inlined)) return inlined;
  const srcById = new Map<string, string>();
  for (const tag of source.match(DECK_IMG_RE) ?? []) {
    const id = tag.match(/\bdata-deck-image="([^"]*)"/i)?.[1];
    const src = tag.match(SRC_RE)?.[1];
    if (id && src && !src.startsWith('data:')) srcById.set(id, src);
  }
  if (srcById.size === 0) return inlined;
  return inlined.replace(DECK_IMG_RE, (tag) => {
    const id = tag.match(/\bdata-deck-image="([^"]*)"/i)?.[1];
    const src = tag.match(SRC_RE)?.[1];
    const original = id ? srcById.get(id) : undefined;
    return original && src?.startsWith('data:') ? tag.replace(SRC_RE, `src="${original}"`) : tag;
  });
}
